import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getRouteApi } from "@tanstack/react-router";

import { api } from "../../lib/api";
import type { CurrentUser } from "../../lib/auth";
import { useUnsavedGuard } from "./unsavedGuard";
import { SavedNote } from "../shared/Icons";
import { toastError } from "../shared/toast";
import { ConversationModal, fmtDate, fmtPhone } from "../shared/ConversationModal";

const _authedRoute = getRouteApi("/_authed");

// ── types ─────────────────────────────────────────────────────────────────────

type Severity = "dica" | "ajuste" | "erro_grave";
type CardStatus = "aberto" | "em_analise" | "resolvido" | "discordado";

interface InsightCard {
  id: string;
  conversation_id: string;
  review_date: string;
  severity: Severity;
  title: string;
  verdict: string;
  excerpt: string | null;
  metrics: Record<string, unknown> | null;
  cost_usd: number;
  status: CardStatus;
  status_by: string | null;
  status_by_name: string | null;
  status_at: string | null;
  resolution_summary: string | null;
  disagree_reason: string | null;
  created_at: string;
  external_user_id: string | null;
  bsuid: string | null;
  lead_name: string | null;
  lead_username: string | null;
  conv_created_at: string | null;
  conv_last_message_at: string | null;
}

interface InsightRun {
  id: string;
  review_date: string;
  trigger: string;
  status: string;
  error: string | null;
  conversations_total: number;
  conversations_reviewed: number;
  cards_created: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  started_at: string;
  ended_at: string | null;
}

// ── helpers ───────────────────────────────────────────────────────────────────

const SEVERITY_ORDER: Record<Severity, number> = { erro_grave: 0, ajuste: 1, dica: 2 };

const SEVERITY_STYLE: Record<Severity, { label: string; bg: string; color: string; border: string }> = {
  erro_grave: { label: "Erro grave", bg: "var(--danger-soft)", color: "var(--danger)", border: "var(--danger-line)" },
  ajuste:     { label: "Ajuste",     bg: "var(--warn-soft)",   color: "var(--warn)",   border: "var(--warn-line)" },
  dica:       { label: "Dica",       bg: "var(--info-soft)",   color: "var(--info)",   border: "var(--info-line)" },
};

const STATUS_LABEL: Record<CardStatus, string> = {
  aberto: "Aberto",
  em_analise: "Em análise",
  resolvido: "Resolvido",
  discordado: "Discordado",
};

const STATUS_STYLE: Record<CardStatus, { bg: string; color: string; border: string }> = {
  aberto:     { bg: "var(--surface-hover)", color: "var(--ink-secondary)", border: "var(--line-strong)" },
  em_analise: { bg: "var(--info-soft)",     color: "var(--info)",          border: "var(--info-line)" },
  resolvido:  { bg: "var(--ok-soft)",       color: "var(--ok)",            border: "var(--ok-line)" },
  discordado: { bg: "var(--warn-soft)",     color: "var(--warn)",          border: "var(--warn-line)" },
};

function fmtUsd(v: number) {
  return `US$ ${Number(v ?? 0).toFixed(4)}`;
}

/** "2026-08-12" → "12/08" */
function fmtReviewDate(iso: string) {
  const [, m, d] = iso.slice(0, 10).split("-");
  return d && m ? `${d}/${m}` : iso;
}

/** Segundos → "1m 23s" ou "45s". */
function fmtDuration(sec: number) {
  if (!Number.isFinite(sec)) return String(sec);
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// Métricas conhecidas → rótulo pt-BR + formatação; o resto cai no genérico.
function fmtMetricEntry(key: string, value: unknown): { label: string; text: string } {
  const num = typeof value === "number" ? value : NaN;
  const k = key.toLowerCase();
  if (k === "avg_response_s") return { label: "Tempo médio de resposta", text: fmtDuration(num) };
  if (k === "max_response_s") return { label: "Tempo máximo de resposta", text: fmtDuration(num) };
  if (k === "responses") return { label: "Respostas da IA", text: String(value) };
  if (k === "unanswered_at_end") return { label: "Cliente sem resposta no fim do dia", text: num > 0 ? "Sim" : "Não" };
  if (k === "msgs_client") return { label: "Msgs do cliente", text: String(value) };
  if (k === "msgs_ai") return { label: "Msgs da IA", text: String(value) };
  if (k === "msgs_human") return { label: "Msgs de humano", text: String(value) };
  return { label: key, text: String(value) };
}

function yesterdayIso() {
  const d = new Date(Date.now() - 864e5);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Identificador do lead: telefone – nome – username – bsuid (só os existentes). */
function leadIdentifier(c: InsightCard) {
  const parts = [
    c.external_user_id ? fmtPhone(c.external_user_id) : null,
    c.lead_name,
    c.lead_username,
    c.bsuid,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" – ") : "—";
}

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const ghostBtn: React.CSSProperties = {
  fontSize: 12,
  padding: "5px 12px",
  borderRadius: 6,
  cursor: "pointer",
  border: "1px solid var(--line-strong)",
  background: "var(--surface-hover)",
  color: "var(--ink-secondary)",
};

// ── componente ────────────────────────────────────────────────────────────────

export function TabInsights({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const routeCtx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const isAdmin = routeCtx.user?.role_name === "Admin";

  const adv = agent.advanced ?? {};

  // ── config por agente (agent.advanced) ──
  const [enabled, setEnabled] = useState<boolean>(!!adv.insights_enabled);
  const [messageCap, setMessageCap] = useState<string>(String(adv.insights_message_cap ?? 100));

  const current = JSON.stringify({ enabled, messageCap });
  const [savedSnap, setSavedSnap] = useState(current);

  const saveMut = useMutation({
    mutationFn: () => {
      const cap = Math.min(500, Math.max(10, parseInt(messageCap) || 100));
      return api.patch(`/agents/${agent.id}`, {
        advanced: { ...adv, insights_enabled: enabled, insights_message_cap: cap },
      });
    },
    onSuccess: () => {
      setSavedSnap(JSON.stringify({ enabled, messageCap }));
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
    },
    onError: (err) => toastError((err as any)?.payload?.detail ?? "Falha ao salvar configuração do revisor."),
  });

  useUnsavedGuard(current !== savedSnap, saveMut.mutateAsync);

  // ── run manual (admin) ──
  const [runDate, setRunDate] = useState<string>(yesterdayIso());
  const [runQueued, setRunQueued] = useState(false);

  const runMut = useMutation({
    mutationFn: () => api.post(`/agents/${agent.id}/insights/run`, { review_date: runDate }),
    onSuccess: () => {
      setRunQueued(true);
      setTimeout(() => setRunQueued(false), 4000);
      qc.invalidateQueries({ queryKey: ["insights-runs", agent.id] });
    },
    onError: (err) => toastError((err as any)?.payload?.detail ?? "Falha ao enfileirar a análise."),
  });

  // ── dados ──
  const { data: cards = [], isLoading } = useQuery<InsightCard[]>({
    queryKey: ["insights-cards", agent.id],
    queryFn: () => api.get(`/agents/${agent.id}/insights/cards`),
  });

  const { data: runs = [] } = useQuery<InsightRun[]>({
    queryKey: ["insights-runs", agent.id],
    queryFn: () => api.get(`/agents/${agent.id}/insights/runs`),
  });

  const statusMut = useMutation({
    mutationFn: ({ cardId, status, note }: { cardId: string; status: CardStatus; note?: string }) =>
      api.post(`/agents/${agent.id}/insights/cards/${cardId}/status`, note ? { status, note } : { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["insights-cards", agent.id] });
      setNoteForm(null);
    },
    onError: (err) => toastError((err as any)?.payload?.detail ?? "Falha ao atualizar o apontamento."),
  });

  const deleteMut = useMutation({
    mutationFn: (cardId: string) => api.delete(`/agents/${agent.id}/insights/cards/${cardId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["insights-cards", agent.id] }),
    onError: (err) => toastError((err as any)?.payload?.detail ?? "Falha ao excluir o apontamento."),
  });

  // ── estado de UI ──
  const [statusFilter, setStatusFilter] = useState<"pendentes" | "todos" | CardStatus>("pendentes");
  const [severityFilter, setSeverityFilter] = useState<"todas" | Severity>("todas");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [noteForm, setNoteForm] = useState<{ cardId: string; action: "resolvido" | "discordado"; text: string } | null>(null);
  const [openConv, setOpenConv] = useState<InsightCard | null>(null);
  const [showRuns, setShowRuns] = useState(false);

  // ── derivados ──
  const openCards = cards.filter((c) => c.status === "aberto");
  const totalRunCost = runs.reduce((acc, r) => acc + (r.cost_usd ?? 0), 0);

  const filtered = cards
    .filter((c) => {
      if (statusFilter === "pendentes") return c.status === "aberto" || c.status === "em_analise";
      if (statusFilter === "todos") return true;
      return c.status === statusFilter;
    })
    .filter((c) => severityFilter === "todas" || c.severity === severityFilter)
    .sort((a, b) => {
      const s = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
      if (s !== 0) return s;
      return b.created_at.localeCompare(a.created_at);
    });

  const submitNote = () => {
    if (!noteForm || !noteForm.text.trim()) return;
    statusMut.mutate({ cardId: noteForm.cardId, status: noteForm.action, note: noteForm.text.trim() });
  };

  return (
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20, maxWidth: 860 }}>
      {/* ── Configuração ── */}
      <div style={{ border: "1px solid var(--line-strong)", borderRadius: 10, padding: 16, display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 8 }}>
          <input
            type="checkbox"
            id="insights-enabled"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            style={{ accentColor: "var(--brand)", width: 16, height: 16 }}
          />
          <label htmlFor="insights-enabled" style={{ fontSize: 13, color: "var(--ink-secondary)", cursor: "pointer" }}>
            Revisor diário
          </label>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={labelStyle}>Analisar últimas X mensagens</label>
          <input
            type="number"
            style={{ ...inputStyle, width: 130 }}
            value={messageCap}
            onChange={(e) => setMessageCap(e.target.value)}
            min={10}
            max={500}
          />
        </div>

        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: "pointer" }}
        >
          {saveMut.isPending ? "Salvando..." : "Salvar"}
        </button>
        <div style={{ paddingBottom: 8 }}>
          <SavedNote show={saveMut.isSuccess} />
        </div>

        {isAdmin && (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, marginLeft: "auto" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Data da análise</label>
              <input
                type="date"
                style={{ ...inputStyle, width: 150 }}
                value={runDate}
                onChange={(e) => setRunDate(e.target.value)}
              />
            </div>
            <button
              onClick={() => runMut.mutate()}
              disabled={runMut.isPending || !runDate}
              style={{ ...ghostBtn, padding: "8px 16px", fontSize: 13 }}
            >
              {runMut.isPending ? "Enfileirando..." : "Analisar agora"}
            </button>
            {runQueued && (
              <span style={{ fontSize: 12, color: "var(--ok)", paddingBottom: 9 }}>Análise enfileirada</span>
            )}
          </div>
        )}
      </div>

      {/* ── Resumo ── */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, fontSize: 12, color: "var(--ink-secondary)" }}>
        <span style={{ fontWeight: 600 }}>Abertos:</span>
        {(["erro_grave", "ajuste", "dica"] as Severity[]).map((sev) => {
          const st = SEVERITY_STYLE[sev];
          const n = openCards.filter((c) => c.severity === sev).length;
          return (
            <span key={sev} style={{ fontSize: 11, padding: "2px 9px", borderRadius: 99, background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
              {n} {st.label.toLowerCase()}
            </span>
          );
        })}
        <span style={{ marginLeft: "auto", color: "var(--ink-muted)" }}>
          Custo total das análises: <span style={{ color: "var(--ink)", fontWeight: 500 }}>{fmtUsd(totalRunCost)}</span>
        </span>
      </div>

      {/* ── Filtros ── */}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <select style={{ ...inputStyle, width: "auto" }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
          <option value="pendentes">Pendentes (abertos + em análise)</option>
          <option value="todos">Todos</option>
          <option value="aberto">Abertos</option>
          <option value="em_analise">Em análise</option>
          <option value="resolvido">Resolvidos</option>
          <option value="discordado">Discordados</option>
        </select>
        <select style={{ ...inputStyle, width: "auto" }} value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value as any)}>
          <option value="todas">Todas as severidades</option>
          <option value="erro_grave">Erro grave</option>
          <option value="ajuste">Ajuste</option>
          <option value="dica">Dica</option>
        </select>
      </div>

      {/* ── Lista de cards ── */}
      {isLoading ? (
        <div style={{ color: "var(--ink-dim)", fontSize: 13 }}>Carregando...</div>
      ) : cards.length === 0 ? (
        <div style={{ color: "var(--ink-dim)", fontSize: 13, padding: "32px 0", textAlign: "center" }}>
          Nenhum apontamento — ligue o revisor diário e os cards aparecem aqui após a análise da madrugada.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ color: "var(--ink-dim)", fontSize: 13, padding: "24px 0", textAlign: "center" }}>
          Nenhum apontamento com esses filtros.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((card) => {
            const sev = SEVERITY_STYLE[card.severity];
            const st = STATUS_STYLE[card.status];
            const expanded = expandedId === card.id;
            const form = noteForm?.cardId === card.id ? noteForm : null;
            return (
              <div key={card.id} style={{ border: "1px solid var(--line)", borderRadius: 10, background: "var(--surface-card)" }}>
                {/* Cabeçalho do card */}
                <div
                  onClick={() => setExpandedId(expanded ? null : card.id)}
                  style={{ padding: "12px 16px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 6 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 99, background: sev.bg, color: sev.color, border: `1px solid ${sev.border}`, fontWeight: 600 }}>
                      {sev.label}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{card.title}</span>
                    <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{fmtReviewDate(card.review_date)}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11, padding: "2px 9px", borderRadius: 99, background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                      {STATUS_LABEL[card.status]}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-secondary)" }}>{leadIdentifier(card)}</div>
                  {card.status === "resolvido" && (
                    <div style={{ fontSize: 11, color: "var(--ok)" }}>
                      Resolvido por {card.status_by_name ?? "—"}{card.status_at ? ` em ${fmtDate(card.status_at)}` : ""}
                      {card.resolution_summary && (
                        <span style={{ color: "var(--ink-muted)" }}> — {card.resolution_summary}</span>
                      )}
                    </div>
                  )}
                  {card.status === "discordado" && card.disagree_reason && (
                    <div style={{ fontSize: 11, color: "var(--warn)" }}>Motivo: {card.disagree_reason}</div>
                  )}
                </div>

                {/* Detalhe expandido */}
                {expanded && (
                  <div style={{ borderTop: "1px solid var(--line-soft)", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ fontSize: 13, color: "var(--ink)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{card.verdict}</div>

                    {card.excerpt && (
                      <div style={{ fontSize: 12, color: "var(--ink-secondary)", fontStyle: "italic", borderLeft: "3px solid var(--line-strong)", paddingLeft: 10, whiteSpace: "pre-wrap" }}>
                        {card.excerpt}
                      </div>
                    )}

                    {card.metrics && Object.keys(card.metrics).length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12, color: "var(--ink-secondary)" }}>
                        {Object.entries(card.metrics).map(([k, v]) => {
                          const m = fmtMetricEntry(k, v);
                          return (
                            <span key={k}>
                              <span style={{ color: "var(--ink-muted)" }}>{m.label}:</span> {m.text}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>Custo desta análise: {fmtUsd(card.cost_usd)}</div>

                    {/* Ações */}
                    {!form ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        <button style={ghostBtn} onClick={() => setOpenConv(card)}>Abrir conversa</button>
                        {card.status === "aberto" && (
                          <>
                            <button style={ghostBtn} disabled={statusMut.isPending}
                              onClick={() => statusMut.mutate({ cardId: card.id, status: "em_analise" })}>
                              Analisar
                            </button>
                            <button style={ghostBtn}
                              onClick={() => setNoteForm({ cardId: card.id, action: "discordado", text: "" })}>
                              Discordar
                            </button>
                          </>
                        )}
                        {card.status === "em_analise" && (
                          <>
                            <button style={{ ...ghostBtn, borderColor: "var(--ok-line)", color: "var(--ok)", background: "var(--ok-soft)" }}
                              onClick={() => setNoteForm({ cardId: card.id, action: "resolvido", text: "" })}>
                              Resolver
                            </button>
                            <button style={ghostBtn}
                              onClick={() => setNoteForm({ cardId: card.id, action: "discordado", text: "" })}>
                              Discordar
                            </button>
                            <button style={ghostBtn} disabled={statusMut.isPending}
                              onClick={() => statusMut.mutate({ cardId: card.id, status: "aberto" })}>
                              Reabrir
                            </button>
                          </>
                        )}
                        {(card.status === "resolvido" || card.status === "discordado") && (
                          <button style={ghostBtn} disabled={statusMut.isPending}
                            onClick={() => statusMut.mutate({ cardId: card.id, status: "aberto" })}>
                            Reabrir
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            style={{ ...ghostBtn, color: "var(--danger)", marginLeft: "auto" }}
                            disabled={deleteMut.isPending}
                            onClick={() => {
                              if (window.confirm("Excluir este apontamento? Essa ação não pode ser desfeita.")) {
                                deleteMut.mutate(card.id);
                              }
                            }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    ) : (
                      /* Mini-form de nota (Resolver / Discordar) */
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <label style={labelStyle}>
                          {form.action === "resolvido" ? "O que foi feito para resolver?" : "Por que discorda deste apontamento?"}
                        </label>
                        <textarea
                          value={form.text}
                          onChange={(e) => setNoteForm({ ...form, text: e.target.value })}
                          autoFocus
                          style={{ ...inputStyle, width: "100%", height: 70, resize: "vertical", lineHeight: 1.5 }}
                        />
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            style={{ ...ghostBtn, background: "var(--brand)", color: "#fff", border: "none", padding: "6px 14px" }}
                            disabled={statusMut.isPending || !form.text.trim()}
                            onClick={submitNote}
                          >
                            {statusMut.isPending ? "Enviando..." : form.action === "resolvido" ? "Marcar como resolvido" : "Confirmar discordância"}
                          </button>
                          <button style={ghostBtn} onClick={() => setNoteForm(null)}>Cancelar</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Execuções do revisor ── */}
      <div style={{ border: "1px solid var(--line)", borderRadius: 10 }}>
        <button
          onClick={() => setShowRuns(!showRuns)}
          style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "var(--ink-secondary)", display: "flex", alignItems: "center", gap: 8 }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ transform: showRuns ? "rotate(90deg)" : "none", transition: "transform .12s" }}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
          Execuções do revisor
        </button>
        {showRuns && (
          <div style={{ borderTop: "1px solid var(--line-soft)", padding: "8px 16px 14px" }}>
            {runs.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "8px 0" }}>Nenhuma execução ainda.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "var(--ink-muted)", textAlign: "left" }}>
                    {["Data analisada", "Origem", "Status", "Conversas", "Cards", "Custo"].map((h) => (
                      <th key={h} style={{ padding: "6px 8px", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} style={{ borderTop: "1px solid var(--surface-hover)", color: "var(--ink-secondary)" }}>
                      <td style={{ padding: "7px 8px" }}>{fmtReviewDate(r.review_date)}</td>
                      <td style={{ padding: "7px 8px" }}>{r.trigger === "manual" ? "Manual" : "Automática"}</td>
                      <td style={{ padding: "7px 8px" }}>
                        {r.status === "error" ? (
                          <span style={{ color: "var(--danger)" }} title={r.error ?? undefined}>
                            Erro{r.error ? ` — ${r.error}` : ""}
                          </span>
                        ) : r.error ? (
                          <span style={{ color: "var(--warn)" }} title={r.error}>Concluída — {r.error}</span>
                        ) : r.status === "running" ? (
                          "Em execução"
                        ) : (
                          "Concluída"
                        )}
                      </td>
                      <td style={{ padding: "7px 8px" }}>{r.conversations_reviewed}/{r.conversations_total}</td>
                      <td style={{ padding: "7px 8px" }}>{r.cards_created}</td>
                      <td style={{ padding: "7px 8px" }}>{fmtUsd(r.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Modal de conversa */}
      {openConv && (
        <ConversationModal
          agentId={agent.id}
          convId={openConv.conversation_id}
          phone={openConv.external_user_id ?? undefined}
          bsuid={openConv.bsuid ?? undefined}
          lead={
            openConv.lead_name || openConv.lead_username
              ? { name: openConv.lead_name, username: openConv.lead_username, ctwa: null, firstSeenAt: null, lastSeenAt: null }
              : undefined
          }
          createdAt={openConv.conv_created_at ?? undefined}
          lastMessageAt={openConv.conv_last_message_at ?? undefined}
          variant="whatsapp"
          onClose={() => setOpenConv(null)}
        />
      )}
    </div>
  );
}
